import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { 
  DocumentTextIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  CheckCircleIcon,
  CreditCardIcon,
  TrashIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { EditInvoiceModal } from '../../components/modals/EditInvoiceModal';
import { AddPaymentModal } from '../../components/modals/AddPaymentModal';
import { InvoiceAssignments } from '../../components/invoice/InvoiceAssignments';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

interface Invoice {
  id: number;
  invoice_number: string;
  client_name: string;
  client_email: string;
  client_tax_id?: string;
  amount_gross: number;
  currency: string;
  issue_date: string;
  due_date: string;
  payment_date?: string | null;
  status: string;
  reminder_count: number;
  total_paid?: number;
  remaining_amount?: number;
  assignments?: Array<{
    id: number;
    assignedUser: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
    };
  }>;
}

export const Invoices = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [itemsPerPage] = useState(25); // Zwiększamy do 25 na stronę
  const [dashboardStats, setDashboardStats] = useState({
    totalInvoices: 0,
    overdueInvoices: 0,
    totalUnpaidAmount: 0,
    paidAmountThisMonth: 0
  });
  const [searchFilters, setSearchFilters] = useState({
    invoice_number: '',
    client_name: '',
    status: '',
    date_from: '',
    date_to: ''
  });
  const { showNotification } = useNotification();

  // Check if user is invoice viewer only
  const isInvoiceViewerOnly = user?.roles?.some((role: any) => role.name === 'invoice_viewer_only');

  useEffect(() => {
    fetchInvoices();
    fetchDashboardStats();
  }, [currentPage]);

  useEffect(() => {
    // Resetuj stronę do 1 gdy zmieniają się filtry
    setCurrentPage(1);
  }, [searchFilters]);

  useEffect(() => {
    // Fetch invoices when filters change
    fetchInvoices();
  }, [searchFilters]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sort_by: 'due_date',
        sort_order: 'ASC'
      });
      
      // Add search filters
      Object.entries(searchFilters).forEach(([key, value]) => {
        if (value && value.trim()) {
          params.append(key, value.trim());
        }
      });
      
      const response = await apiService.get(`/invoices?${params.toString()}`);
      setInvoices(response.data.invoices || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalInvoices(response.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch invoices');
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await apiService.get('/invoices/dashboard-stats');
      const stats = response.data;
      setDashboardStats({
        totalInvoices: stats.totalInvoices || 0,
        overdueInvoices: stats.overdueInvoices || 0,
        totalUnpaidAmount: stats.unpaidAmount || 0,
        paidAmountThisMonth: stats.paidAmountThisMonth || 0
      });
    } catch (err: any) {
      console.error('Error fetching dashboard stats:', err);
    }
  };

  const calculateUnpaidAmount = () => {
    return invoices
      .filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled')
      .reduce((sum, inv) => sum + (Number(inv.amount_gross) || 0), 0);
  };

  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setSearchFilters({
      invoice_number: '',
      client_name: '',
      status: '',
      date_from: '',
      date_to: ''
    });
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      'paid': 'success',
      'sent': 'warning', 
      'overdue': 'error',
      'draft': 'gray',
      'cancelled': 'error'
    };

    const labels = {
      'paid': 'Opłacona',
      'sent': 'Wysłana',
      'overdue': 'Zaległa',
      'draft': 'Szkic',
      'cancelled': 'Anulowana'
    };

    return <Badge variant={variants[status] as any}>{labels[status] || status}</Badge>;
  };

  const StatusDropdown = ({ invoice }: { invoice: Invoice }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    // For invoice viewer only users, show only 'paid' option
    const statusOptions = isInvoiceViewerOnly ? [
      { value: 'paid', label: 'Opłacona', color: 'bg-green-100 text-green-800' }
    ] : [
      { value: 'draft', label: 'Projekt', color: 'bg-gray-100 text-gray-800' },
      { value: 'sent', label: 'Wysłana', color: 'bg-blue-100 text-blue-800' },
      { value: 'paid', label: 'Opłacona', color: 'bg-green-100 text-green-800' },
      { value: 'overdue', label: 'Przeterminowana', color: 'bg-red-100 text-red-800' },
      { value: 'cancelled', label: 'Anulowana', color: 'bg-gray-100 text-gray-800' }
    ];

    const currentOption = statusOptions.find(opt => opt.value === invoice.status);

    // If user is invoice viewer only and invoice is already paid, show non-interactive badge
    if (isInvoiceViewerOnly && invoice.status === 'paid') {
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${currentOption?.color}`}>
          {currentOption?.label || invoice.status}
        </span>
      );
    }

    // If user is invoice viewer only and invoice is not paid, show only "Mark as Paid" button
    if (isInvoiceViewerOnly) {
      return (
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => handleStatusChange(invoice.id, 'paid')}
          className="text-green-600 border-green-600 hover:bg-green-50"
        >
          Oznacz jako opłacone
        </Button>
      );
    }

    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${currentOption?.color} hover:opacity-80 transition-opacity`}
        >
          {currentOption?.label || invoice.status}
          <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setIsOpen(false)}
            ></div>
            <div className="absolute right-0 z-20 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200">
              <div className="py-1">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      handleStatusChange(invoice.id, option.value);
                      setIsOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      option.value === invoice.status ? 'bg-gray-50 font-medium' : ''
                    }`}
                  >
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${option.color}`}>
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  const handleEditInvoice = (invoiceId: number) => {
    setSelectedInvoiceId(invoiceId);
    setEditModalOpen(true);
  };

  const handleAddPayment = (invoiceId: number) => {
    setSelectedInvoiceId(invoiceId);
    setPaymentModalOpen(true);
  };

  const handleStatusChange = async (invoiceId: number, newStatus: string) => {
    try {
      const updateData: any = { status: newStatus };
      
      // Automatycznie ustaw payment_date gdy status zmienia się na "paid"
      if (newStatus === 'paid') {
        updateData.payment_date = new Date().toISOString().split('T')[0];
      }

      await apiService.patch(`/invoices/${invoiceId}/status`, updateData);
      
      // Aktualizuj lokalny stan
      setInvoices(invoices.map(invoice => 
        invoice.id === invoiceId 
          ? { ...invoice, status: newStatus, payment_date: newStatus === 'paid' ? updateData.payment_date : null }
          : invoice
      ));
    } catch (err: any) {
      console.error('Error updating invoice status:', err);
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: 'Błąd podczas aktualizacji statusu faktury'
      });
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedInvoiceId(null);
  };

  const handleClosePaymentModal = () => {
    setPaymentModalOpen(false);
    setSelectedInvoiceId(null);
  };

  const handleInvoiceUpdated = () => {
    fetchInvoices();
  };

  const handlePaymentAdded = () => {
    fetchInvoices();
  };

  const handlePreviewInvoice = async (invoiceId: number) => {
    try {
      // Fetch the invoice details
      const response = await apiService.get(`/invoices/${invoiceId}`);
      const invoice = response.data;
      setPreviewInvoice(invoice);
      setPreviewModalOpen(true);
    } catch (err: any) {
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: 'Nie udało się wczytać faktury do podglądu'
      });
    }
  };

  const handleDeleteInvoice = (invoiceId: number) => {
    setInvoiceToDelete(invoiceId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!invoiceToDelete) return;
    
    try {
      setDeleting(true);
      await apiService.delete(`/invoices/${invoiceToDelete}`);
      
      // Remove from local state
      setInvoices(invoices.filter(inv => inv.id !== invoiceToDelete));
      
      showNotification({
        type: 'success',
        title: 'Sukces',
        message: 'Faktura została usunięta'
      });
    } catch (err: any) {
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: err.response?.data?.message || 'Nie udało się usunąć faktury'
      });
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setInvoiceToDelete(null);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Faktury</h1>
        <p className="mt-1 text-sm text-gray-500">
          Zarządzaj fakturami i monitoruj płatności
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Wszystkie faktury
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {dashboardStats.totalInvoices}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Zaległe faktury
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {dashboardStats.overdueInvoices}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 font-bold">zł</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Do zapłaty
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatCurrency(dashboardStats.totalUnpaidAmount, 'PLN')}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Opłacone w tym miesiącu
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatCurrency(dashboardStats.paidAmountThisMonth, 'PLN')}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Wyszukiwanie i filtry</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numer faktury
              </label>
              <input
                type="text"
                placeholder="Wpisz numer faktury..."
                value={searchFilters.invoice_number}
                onChange={(e) => handleSearchChange('invoice_number', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Klient
              </label>
              <input
                type="text"
                placeholder="Nazwa klienta..."
                value={searchFilters.client_name}
                onChange={(e) => handleSearchChange('client_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={searchFilters.status}
                onChange={(e) => handleSearchChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Wszystkie</option>
                <option value="draft">Szkic</option>
                <option value="sent">Wysłana</option>
                <option value="paid">Opłacona</option>
                <option value="overdue">Zaległa</option>
                <option value="cancelled">Anulowana</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data od
              </label>
              <input
                type="date"
                value={searchFilters.date_from}
                onChange={(e) => handleSearchChange('date_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data do
              </label>
              <input
                type="date"
                value={searchFilters.date_to}
                onChange={(e) => handleSearchChange('date_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="mr-2"
            >
              Wyczyść filtry
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Lista faktur ({totalInvoices})</CardTitle>
            <Button>
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              Nowa faktura
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Ładowanie faktur...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={fetchInvoices} variant="outline">
                Spróbuj ponownie
              </Button>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Numer faktury
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Klient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    NIP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kwota
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data wystawienia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Termin płatności
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opiekunowie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {invoice.invoice_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{invoice.client_name}</div>
                        <div className="text-gray-500 text-xs">{invoice.client_email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.client_tax_id || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">
                          {formatCurrency(invoice.amount_gross, invoice.currency)}
                        </div>
                        {(invoice.total_paid || 0) > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            <div className="text-green-600">
                              Zapłacono: {formatCurrency(invoice.total_paid || 0, invoice.currency)}
                            </div>
                            {(invoice.remaining_amount || 0) > 0 && (
                              <div className="text-orange-600">
                                Pozostało: {formatCurrency(invoice.remaining_amount || 0, invoice.currency)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invoice.issue_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div>{formatDate(invoice.due_date)}</div>
                        {invoice.status === 'paid' && invoice.payment_date && (
                          <div className="flex items-center text-xs text-green-600 mt-1">
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Opłacono: {formatDate(invoice.payment_date)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusDropdown invoice={invoice} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(invoice as any).assignments?.length > 0 ? (
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 mr-1 text-gray-400" />
                          <span className="text-sm font-medium">
                            {(invoice as any).assignments.length}
                          </span>
                          <span className="text-xs text-gray-400 ml-1">
                            {(invoice as any).assignments.length === 1 ? 'opiekun' : 'opiekunów'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Brak opiekunów</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handlePreviewInvoice(invoice.id)}
                          title="Podgląd faktury"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Button>
                        {/* Hide edit/delete buttons for invoice viewer only users */}
                        {!isInvoiceViewerOnly && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleEditInvoice(invoice.id)}
                              title="Edytuj fakturę"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            {invoice.status !== 'paid' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleAddPayment(invoice.id)}
                                title="Dodaj płatność"
                              >
                                <CreditCardIcon className="h-4 w-4" />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              title="Usuń fakturę"
                              className="text-red-600 hover:text-red-800 hover:border-red-300"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" title="Pobierz fakturę">
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        </Button>
                      </div>
                      {invoice.status === 'paid' && (
                        <div className="text-xs text-green-600 mt-1">
                          Przypomnienia wyłączone
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Pokazywane <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> do{' '}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalInvoices)}</span> z{' '}
                  <span className="font-medium">{totalInvoices}</span> faktur
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                  >
                    Poprzednia
                  </Button>
                  
                  {/* Page numbers */}
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = Math.max(1, currentPage - 2) + i;
                      if (page > totalPages) return null;
                      
                      return (
                        <Button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          variant={currentPage === page ? "primary" : "outline"}
                          size="sm"
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                  >
                    Następna
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Invoice Modal */}
      <EditInvoiceModal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        invoiceId={selectedInvoiceId}
        onInvoiceUpdated={handleInvoiceUpdated}
      />

      {/* Add Payment Modal */}
      <AddPaymentModal
        isOpen={paymentModalOpen}
        onClose={handleClosePaymentModal}
        invoiceId={selectedInvoiceId}
        onPaymentAdded={handlePaymentAdded}
      />

      {/* Preview Modal */}
      {previewModalOpen && previewInvoice && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Podgląd faktury {previewInvoice.invoice_number}
                </h2>
                <Button
                  variant="outline"
                  onClick={() => setPreviewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </Button>
              </div>

              {/* Invoice Content */}
              <div className="space-y-6">
                {/* Client Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Dane klienta</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p><strong>Nazwa:</strong> {previewInvoice.client_name}</p>
                    <p><strong>Email:</strong> {previewInvoice.client_email}</p>
                    {previewInvoice.client_phone && <p><strong>Telefon:</strong> {previewInvoice.client_phone}</p>}
                    {previewInvoice.client_address && <p><strong>Adres:</strong> {previewInvoice.client_address}</p>}
                    {previewInvoice.client_tax_id && <p><strong>NIP:</strong> {previewInvoice.client_tax_id}</p>}
                  </div>
                </div>

                {/* Invoice Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Szczegóły faktury</h3>
                  <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-2 gap-4">
                    <p><strong>Data wystawienia:</strong> {formatDate(previewInvoice.issue_date)}</p>
                    <p><strong>Termin płatności:</strong> {formatDate(previewInvoice.due_date)}</p>
                    <p><strong>Status:</strong> <Badge variant={previewInvoice.status === 'paid' ? 'success' : previewInvoice.status === 'overdue' ? 'error' : 'warning'}>{previewInvoice.status}</Badge></p>
                    <p><strong>Waluta:</strong> {previewInvoice.currency}</p>
                    <p><strong>Kwota netto:</strong> {formatCurrency(previewInvoice.amount_net, previewInvoice.currency)}</p>
                    <p><strong>VAT:</strong> {formatCurrency(previewInvoice.amount_tax, previewInvoice.currency)}</p>
                    <p><strong>Kwota brutto:</strong> <span className="font-semibold">{formatCurrency(previewInvoice.amount_gross, previewInvoice.currency)}</span></p>
                  </div>
                </div>

                {/* Invoice Items */}
                {previewInvoice.items && previewInvoice.items.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Pozycje faktury</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nazwa</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ilość</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cena netto</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">VAT%</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Wartość brutto</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {previewInvoice.items.map((item: any, index: number) => (
                            <tr key={index}>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <div className="font-medium">{item.name}</div>
                                {item.description && <div className="text-gray-500 text-xs">{item.description}</div>}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity} {item.unit}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(item.unit_price_net, previewInvoice.currency)}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.tax_rate}%</td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                {formatCurrency(item.total_gross || (item.quantity * item.unit_price_net * (1 + item.tax_rate/100)), previewInvoice.currency)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Description */}
                {previewInvoice.description && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Opis</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700">{previewInvoice.description}</p>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {previewInvoice.notes && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Uwagi</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700">{previewInvoice.notes}</p>
                    </div>
                  </div>
                )}

                {/* Invoice Assignments */}
                <div>
                  <InvoiceAssignments invoiceId={previewInvoice.id} />
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => window.print()}
                >
                  Drukuj
                </Button>
                <Button
                  onClick={() => setPreviewModalOpen(false)}
                >
                  Zamknij
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Potwierdź usunięcie
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Czy na pewno chcesz usunąć tę fakturę? Ta operacja jest nieodwracalna.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                disabled={deleting}
              >
                Anuluj
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Usuwanie...
                  </>
                ) : (
                  'Usuń fakturę'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};